import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Sun, Moon, Monitor } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import Logo from '../common/Logo'

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { user } = useAuth()
  const { theme, setTheme } = useTheme()
  const location = useLocation()
  
  // Detectar scroll para mudar o estilo do header
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  // Fechar menu ao mudar de rota
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location])

  // Get effective theme (resolve 'system' to actual theme)
  const getEffectiveTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return theme
  }

  // Toggle theme function
  const toggleTheme = () => {
    const effectiveTheme = getEffectiveTheme()
    if (effectiveTheme === 'light') {
      setTheme('dark')
    } else {
      setTheme('light')
    }
  }

  // Get theme icon (based on effective theme)
  const getThemeIcon = () => {
    const effectiveTheme = getEffectiveTheme()
    if (effectiveTheme === 'light') return <Sun className="w-5 h-5" />
    return <Moon className="w-5 h-5" />
  }

  // Smooth scroll to section
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault()
    const element = document.getElementById(sectionId)
    if (element) {
      const headerOffset = 80 // Account for fixed header height
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
    setIsMenuOpen(false) // Close mobile menu after clicking
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled
        ? 'bg-surface dark:bg-ink-700 shadow-caderno-md py-3'
        : 'bg-transparent py-5'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <Logo variant="lockup" size={40} />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              className={`${
                isScrolled
                  ? 'text-ink-700 dark:text-cream-300'
                  : 'text-ink-700 dark:text-cream-100'
              } hover:text-terra-500 transition-colors cursor-pointer`}
            >
              Início
            </a>
            <Link to="/avaliacoes" className={`${
              isScrolled
                ? 'text-ink-700 dark:text-cream-300'
                : 'text-ink-700 dark:text-cream-100'
            } hover:text-terra-500 transition-colors`}>
              Banco de Atividades
            </Link>
            <Link to="/blog" className={`${
              isScrolled
                ? 'text-ink-700 dark:text-cream-300'
                : 'text-ink-700 dark:text-cream-100'
            } hover:text-terra-500 transition-colors`}>
              Blog
            </Link>
            <a
              href="#beneficios"
              onClick={(e) => scrollToSection(e, 'beneficios')}
              className={`${
                isScrolled
                  ? 'text-ink-700 dark:text-cream-300'
                  : 'text-ink-700 dark:text-cream-100'
              } hover:text-terra-500 transition-colors cursor-pointer`}
            >
              Recursos
            </a>
            <a
              href="#pricing"
              onClick={(e) => scrollToSection(e, 'pricing')}
              className={`${
                isScrolled
                  ? 'text-ink-700 dark:text-cream-300'
                  : 'text-ink-700 dark:text-cream-100'
              } hover:text-terra-500 transition-colors cursor-pointer`}
            >
              Preços
            </a>
            <Link
              to="/contact"
              className={`${
                isScrolled
                  ? 'text-ink-700 dark:text-cream-300'
                  : 'text-ink-700 dark:text-cream-100'
              } hover:text-terra-500 transition-colors`}
            >
              Contato
            </Link>
          </nav>
          
          {/* Theme Toggle and Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-caderno-sm transition-colors ${
                isScrolled
                  ? 'text-ink-500 dark:text-ink-300 hover:bg-cream-100 dark:hover:bg-ink-700'
                  : 'text-ink-700 dark:text-cream-300 hover:bg-white/10'
              }`}
              title={`Alternar para tema ${getEffectiveTheme() === 'light' ? 'escuro' : 'claro'}`}
            >
              {getThemeIcon()}
            </button>

            {user ? (
              <Link
                to="/app"
                className="px-5 py-2 bg-terra-500 hover:bg-terra-700 text-white rounded-caderno-sm transition-colors"
              >
                Meu Painel
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`px-5 py-2 transition-colors ${
                    isScrolled
                      ? 'text-ink-700 dark:text-cream-300 hover:text-terra-500'
                      : 'text-ink-700 dark:text-cream-300 hover:text-terra-500'
                  }`}
                >
                  Entrar
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2 bg-terra-500 hover:bg-terra-700 text-white rounded-caderno-sm transition-colors"
                >
                  Criar Conta
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 rounded-caderno-sm transition-colors ${
              isScrolled
                ? 'text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700'
                : 'text-ink-700 dark:text-cream-300 hover:bg-white/10'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-surface dark:bg-ink-700 border-t border-cream-400 dark:border-ink-500 mt-2">
          <div className="container mx-auto px-4 py-4">
            <nav className="flex flex-col space-y-4">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                  setIsMenuOpen(false)
                }}
                className="px-4 py-2 text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors cursor-pointer"
              >
                Início
              </a>
              <Link
                to="/avaliacoes"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-2 text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors"
              >
                Banco de Atividades
              </Link>
              <Link
                to="/blog"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-2 text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors"
              >
                Blog
              </Link>
              <a
                href="#beneficios"
                onClick={(e) => scrollToSection(e, 'beneficios')}
                className="px-4 py-2 text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors cursor-pointer"
              >
                Recursos
              </a>
              <a
                href="#pricing"
                onClick={(e) => scrollToSection(e, 'pricing')}
                className="px-4 py-2 text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors cursor-pointer"
              >
                Preços
              </a>
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-2 text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors"
              >
                Contato
              </Link>

              {/* Theme Toggle Mobile */}
              <button
                onClick={toggleTheme}
                className="w-full flex items-center space-x-2 px-4 py-2 text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors"
              >
                {getThemeIcon()}
                <span>Alternar tema</span>
              </button>
              
              <div className="pt-4 border-t border-cream-400 dark:border-ink-500">
                {user ? (
                  <Link
                    to="/app"
                    className="w-full px-4 py-2 bg-terra-500 hover:bg-terra-700 text-white rounded-caderno-sm transition-colors block text-center"
                  >
                    Meu Painel
                  </Link>
                ) : (
                  <div className="flex flex-col space-y-2">
                    <Link
                      to="/login"
                      className="px-4 py-2 text-ink-700 dark:text-cream-300 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm transition-colors block text-center"
                    >
                      Entrar
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 bg-terra-500 hover:bg-terra-700 text-white rounded-caderno-sm transition-colors block text-center"
                    >
                      Criar Conta
                    </Link>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  )
}

export default Header